import { WIDE_SHELL_MIN_WIDTH } from '@/chat/useShellWidth';
import {
  useRef,
  useState,
  useEffect,
  useCallback,
  type CSSProperties,
} from 'react';
import { useChatContext } from '@/chat/ChatContext';
import { getLabel } from '@/i18n/maps';
import {
  BookIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@/components/icons';
import type { Blog } from '@/types/chat';
import { useDragScroll } from '@/utils/useDragScroll';

export interface BlogSuggestionsProps {
  blogs: Blog[];
}


/**
 * Renders the assistant's `provider_payload.blogs` at the end of a reply
 * as a horizontal carousel — three cards per row on desktop, one per row
 * on narrow widths. Cards are scroll-snapped and chevron-controlled.
 *
 * Card sizing follows the measured chat-shell width (ChatContext.shellWidth),
 * not the viewport, matching how the side panel is gated.
 */
export function BlogSuggestions({ blogs }: BlogSuggestionsProps): JSX.Element | null {
  const { locale, shellWidth } = useChatContext();
  const renderableBlogs = (blogs ?? []).filter((blog) => Boolean(blog.title && blog.url));
  const trackRef = useRef<HTMLUListElement | null>(null);
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);

  // 3-up on desktop, 1-up on narrow widgets — driven by the SAME measured
  // width the parent shell uses, so the breakpoint is always coherent
  // with the side-panel/drawer split.
  const isWide = shellWidth != null && shellWidth >= WIDE_SHELL_MIN_WIDTH;
  // Track gap is 12px → with 3 cells the formula is (100% - 24px) / 3.
  const cellStyle: CSSProperties = isWide
    ? { width: 'calc((100% - 24px) / 3)' }
    : { width: '100%' };

  const updateArrows = useCallback(() => {
    const el = trackRef.current;
    if (!el) return;
    setCanPrev(el.scrollLeft > 4);
    setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    updateArrows();
    el.addEventListener('scroll', updateArrows, { passive: true });
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      ro.disconnect();
    };
  }, [updateArrows, renderableBlogs.length]);

  // Mouse drag-to-scroll, settling on a card edge (touch and trackpads scroll natively).
  const dragScroll = useDragScroll(cardStep);

  if (renderableBlogs.length === 0) return null;

  function scrollByDir(direction: 1 | -1) {
    const el = trackRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * cardStep(el), behavior: 'smooth' });
  }

  const title = getLabel('blog_suggestions_title', locale);

  return (
    <section className="cal-blogs tw-mt-4 tw-pt-4 tw-border-t tw-border-cal-border">
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-3">
        <h3 className="tw-font-serif tw-text-[15.5px] tw-font-bold tw-text-cal-ink">
          {title}
        </h3>
        {renderableBlogs.length > 1 && (
          <div className="tw-flex tw-items-center tw-gap-2">
            <button
              type="button"
              onClick={() => scrollByDir(-1)}
              disabled={!canPrev}
              aria-label={getLabel('blog_prev', locale)}
              content-content="chatbot-blog-precedent"
              className="gtmtrack tw-w-9 tw-h-9 tw-rounded-full tw-bg-cal-green-500 tw-text-white tw-flex tw-items-center tw-justify-center hover:tw-bg-cal-green-700 disabled:tw-bg-cal-bg-soft disabled:tw-text-cal-ink-muted disabled:tw-cursor-not-allowed tw-transition-colors"
            >
              <ChevronLeftIcon width={18} height={18} />
            </button>
            <button
              type="button"
              onClick={() => scrollByDir(1)}
              disabled={!canNext}
              aria-label={getLabel('blog_next', locale)}
              content-content="chatbot-blog-suivant"
              className="gtmtrack tw-w-9 tw-h-9 tw-rounded-full tw-bg-cal-green-500 tw-text-white tw-flex tw-items-center tw-justify-center hover:tw-bg-cal-green-700 disabled:tw-bg-cal-bg-soft disabled:tw-text-cal-ink-muted disabled:tw-cursor-not-allowed tw-transition-colors"
            >
              <ChevronRightIcon width={18} height={18} />
            </button>
          </div>
        )}
      </div>
      <ul
        ref={trackRef}
        {...dragScroll}
        className={`cal-blog-track tw-flex tw-gap-3 tw-overflow-x-auto tw-snap-x tw-snap-mandatory tw-scroll-smooth tw-list-none tw-p-0 tw-m-0${
          canPrev || canNext ? ' is-scrollable' : ''
        }`}
      >
        {renderableBlogs.map((b) => (
          <li
            key={b.slug}
            style={cellStyle}
            className="tw-snap-start tw-flex-shrink-0"
          >
            <BlogCard blog={b} />
          </li>
        ))}
      </ul>
    </section>
  );
}

/** One card plus the 12px track gap. */
function cardStep(track: HTMLElement): number {
  const first = track.firstElementChild as HTMLElement | null;
  return first ? first.offsetWidth + 12 : track.clientWidth;
}

function BlogCard({ blog }: { blog: Blog }): JSX.Element {
  return (
    <a
      href={blog.url}
      target="_blank"
      rel="noopener noreferrer"
      content-content="chatbot-blog-carte"
      className="gtmtrack tw-group tw-flex tw-flex-col tw-h-full tw-rounded-2xl tw-bg-cal-surface tw-border tw-border-cal-border tw-overflow-hidden tw-no-underline hover:tw-border-cal-green-500 hover:tw-shadow-cal-md tw-transition-all"
    >
      <div className="tw-aspect-[16/10] tw-bg-cal-bg-soft tw-overflow-hidden">
        {blog.image ? (
          <img
            src={blog.image}
            alt=""
            loading="lazy"
            className="tw-w-full tw-h-full tw-object-cover group-hover:tw-scale-[1.03] tw-transition-transform tw-duration-300"
          />
        ) : (
          <div className="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center tw-text-cal-green-700">
            <BookIcon width={26} height={26} />
          </div>
        )}
      </div>
      <div className="tw-flex tw-flex-col tw-flex-1 tw-p-3">
        <h4 className="tw-font-serif tw-font-semibold tw-text-[14px] tw-text-cal-ink tw-leading-snug tw-line-clamp-2 tw-no-underline group-hover:tw-text-cal-green-700 tw-transition-colors">
          {blog.title}
        </h4>
      </div>
    </a>
  );
}
